"use client";

import { LogoWithText } from "@plura/client/components";
import { cn } from "@plura/shared/utils";
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "../../ui/Button";

interface CompareRow {
  metric: string;
  competitor: string;
  formsuite: string;
}

interface Competitor {
  id: string;
  tab: string;
  title: string;
  caption: string;
  cta: { label: string; href?: string; scrollTo?: string; focus?: boolean };
  secondary?: { label: string; scrollTo: string };
  rows: CompareRow[];
}

const SIGN_UP = "https://app.formsuite.co/sign-up";
// Where the import form lives — used as the navigation fallback when this
// component is dropped on a page without the import section.
const TYPEFORM_PAGE = "/typeform-alternative";
// How long each item stays active before auto-advancing (matches the progress line).
const CYCLE_MS = 6000;

const COMPETITORS: Competitor[] = [
  {
    id: "typeform",
    tab: "Typeform",
    title: "Beautiful conversational forms. Without the Typeform price tag.",
    caption:
      "Formsuite gives you a modern, Typeform-style form experience with multi-step layouts, conditional logic, answer recall, calculations, hidden fields, AI-powered insights, and flexible sharing. Built for teams that want premium forms without paying enterprise-level prices.",
    cta: { label: "Import from Typeform", scrollTo: "import", focus: true },
    secondary: { label: "See full comparison", scrollTo: "comparison" },
    rows: [
      { metric: "Monthly cost", competitor: "$79/mo", formsuite: "$0" },
      { metric: "Number of responses", competitor: "1000/mo", formsuite: "Unlimited" },
      { metric: "Form analytics", competitor: "$129/mo plan", formsuite: "Included, advanced at $24/mo" },
      { metric: "Time to migrate", competitor: "", formsuite: "~30 seconds" },
    ],
  },
  {
    id: "google-forms",
    tab: "Google Forms",
    title: "Modern forms that actually feel on-brand.",
    caption:
      "Google Forms is simple, but it still feels basic. Formsuite helps you create polished, multi-step forms with branded themes, logic, calculations, answer recall, file uploads, partial submissions, AI summaries, and better response insights — without the outdated form-builder feel.",
    cta: { label: "Create a Free Form", href: SIGN_UP },
    rows: [
      {
        metric: "Form experience",
        competitor: "Basic single-page",
        formsuite: "Modern multi-step"
      },
      {
        metric: "Visual design",
        competitor: "Limited customization",
        formsuite: "Beautiful branded themes"
      },
      {
        metric: "Advanced logic",
        competitor: "Basic branching",
        formsuite: "Logic, calculations & answer recall"
      },
      {
        metric: "Partial submissions",
        competitor: "Not built-in",
        formsuite: "Built-in"
      },
      {
        metric: "Response insights",
        competitor: "Basic responses",
        formsuite: "Analytics, summaries & AI insights"
      },
    ],
  },
  {
    id: "jotform",
    tab: "Jotform",
    title: "Powerful forms, without the clutter.",
    caption:
      "Jotform gives you a lot, but it can feel heavy when you just want to build beautiful, high-converting forms fast. Formsuite gives you modern multi-step forms, branded design, partial submissions, logic, calculations, integrations, webhooks, and AI-powered response insights in a cleaner, easier workspace.",
    cta: { label: "Create a Free Form", href: SIGN_UP },
    rows: [
      {
        metric: "Free submissions",
        competitor: "1000/mo",
        formsuite: "Unlimited"
      },
      {
        metric: "Form limit",
        competitor: "25",
        formsuite: "Unlimited"
      },
      {
        metric: "AI insights",
        competitor: "Limited / add-on focused",
        formsuite: "Built-in"
      },
      {
        metric: "Form experience",
        competitor: "Powerful but busy",
        formsuite: "Clean, modern, multi-step"
      },
    ],
  },
];

/** Scroll to a section on this page (and optionally focus its input). When the
 * section isn't on the current page, navigate to the Typeform page with the hash. */
const goTo = (id: string, focus = false) => {
  const el = document.getElementById(id);
  if (!el) {
    window.location.assign(`${TYPEFORM_PAGE}#${id}`);
    return;
  }
  // Fixed site header is 80px — offset so the target isn't overlapped.
  const top = el.getBoundingClientRect().top + window.scrollY - 96;
  window.scrollTo({ top, behavior: "smooth" });
  if (focus) window.setTimeout(() => el.querySelector<HTMLInputElement>("input")?.focus(), 600);
};

const GRID = "grid grid-cols-[1.4fr_1fr_1fr]";

const ComparisonCard = ({ competitor, className }: { competitor: Competitor; className?: string }) => {
  const rowsRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  // Animate the rows area height as the row count changes between alternatives.
  useLayoutEffect(() => {
    if (rowsRef.current) setHeight(rowsRef.current.scrollHeight);
  }, [competitor.id]);

  return (
    <div
      className={cn(
        "w-full max-w-[560px] overflow-hidden rounded-2xl border border-[#e5e5e5] bg-white shadow-[0_24px_60px_rgba(18,18,18,0.12)]",
        className,
      )}
    >
      {/* Header (persists across switches) */}
      <div className={GRID}>
        <div className="px-5 py-3.5 max-md:px-3" />
        <div className="px-3 py-3.5 text-center font-body text-[11px] font-bold uppercase tracking-[0.04em] text-[#555555]">
          {competitor.tab}
        </div>
        <div className="bg-green flex items-center justify-center px-3 py-3 [&_p]:!text-[15px] [&_svg]:!w-[22px]">
          <LogoWithText darkMode />
        </div>
      </div>

      {/* Rows — height animates, content cross-fades on switch */}
      <div style={{ height }} className="overflow-hidden transition-[height] duration-300 ease-out">
        <div key={competitor.id} ref={rowsRef} className={cn(GRID, "animate-[switch-fade-in_300ms_ease-out]")}>
          {competitor.rows.map((row) => (
            <Fragment key={row.metric}>
              <div className="border-t border-[#e5e5e5] px-5 py-4 font-body text-sm font-medium text-dark max-md:px-3 max-md:text-[13px]">
                {row.metric}
              </div>
              <div className="border-t border-[#e5e5e5] px-3 py-4 text-center font-body text-sm text-[#555555] max-md:text-[13px]">
                {row.competitor || "—"}
              </div>
              <div className="bg-green/[0.05] border-t border-[#e5e5e5] px-3 py-4 text-center font-body text-sm font-semibold text-dark max-md:text-[13px]">
                {row.formsuite}
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const SwitchComparison = ({
  autoCycle = false,
  reverse = false,
}: {
  autoCycle?: boolean;
  /** Put the comparison on the left and the content on the right. */
  reverse?: boolean;
}) => {
  const [activeId, setActiveId] = useState(COMPETITORS[0].id);
  const active = COMPETITORS.find((c) => c.id === activeId) ?? COMPETITORS[0];

  // Auto-advance to the next alternative after the progress line completes.
  useEffect(() => {
    if (!autoCycle) return;
    const idx = COMPETITORS.findIndex((c) => c.id === activeId);
    const nextId = COMPETITORS[(idx + 1) % COMPETITORS.length].id;
    const t = window.setTimeout(() => setActiveId(nextId), CYCLE_MS);
    return () => window.clearTimeout(t);
  }, [activeId, autoCycle]);

  return (
    <section className="bg-canvas section-spacing">
      <div className="landing-container flex flex-col gap-12">
        {/* Header — aligned with the content column (left by default, right when reversed) */}
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <div className={cn("flex flex-col gap-5", reverse && "lg:col-start-2")}>
            <span className="font-body text-sm font-medium uppercase tracking-[0.12em] text-[#676960]/80">
              Switch &amp; save
            </span>
            <h2 className="text-h2-medium font-heading text-dark">Switch to Formsuite in one click.</h2>
          </div>
        </div>

        {/* Main — accordion + CTAs on one side, yellow square on the other */}
        <div className="grid items-stretch gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Content */}
          <div className={cn("flex flex-col justify-between gap-10", reverse && "lg:order-2")}>
            <div className="flex flex-col">
              {COMPETITORS.map((comp) => {
                const isOpen = comp.id === activeId;
                return (
                  <button
                    key={comp.id}
                    type="button"
                    onClick={() => setActiveId(comp.id)}
                    className={cn(
                      "relative w-full overflow-hidden text-left transition-all duration-300 ease-out",
                      isOpen
                        ? "bg-shade rounded-2xl p-8 max-md:p-6"
                        : "border-b border-black/[0.06] px-2 py-6 opacity-40 hover:opacity-70",
                    )}
                  >
                    {isOpen ? (
                      <div className="flex flex-col gap-3">
                        <h3 className="font-body text-2xl font-bold leading-tight text-dark max-md:text-xl">
                          {comp.tab}
                        </h3>
                        <p className="font-body text-base leading-relaxed text-[#66685f]">{comp.title}</p>
                      </div>
                    ) : (
                      <h3 className="font-body text-2xl font-medium leading-tight text-[#323232] max-md:text-xl">
                        {comp.tab}
                      </h3>
                    )}

                    {/* Active = an 80px salmon line that travels across while auto-cycling.
                        Hidden entirely when cycling is off. */}
                    {isOpen && autoCycle && (
                      <span
                        aria-hidden
                        className="from-salmon/0 via-salmon to-salmon/0 absolute bottom-0 h-[3px] w-20 bg-gradient-to-r animate-[switch-progress_6000ms_linear]"
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3">
              {active.cta.scrollTo ? (
                <Button onClick={() => goTo(active.cta.scrollTo!, active.cta.focus)}>{active.cta.label}</Button>
              ) : (
                <Button href={active.cta.href!}>{active.cta.label}</Button>
              )}
              {active.secondary && (
                <Button variant="secondary" onClick={() => goTo(active.secondary!.scrollTo)}>
                  {active.secondary.label}
                </Button>
              )}
            </div>
          </div>

          {/* Yellow square; table anchored to the bottom, cropped through its own
              bottom padding so no content is hidden. */}
          <div
            className={cn(
              "bg-yellow flex items-end justify-center overflow-hidden rounded-[48px] px-8 pt-12 max-md:rounded-3xl max-md:px-6 max-md:pt-8",
              reverse && "lg:order-1",
            )}
          >
            <ComparisonCard competitor={active} className="translate-y-8 pb-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SwitchComparison;
